import React, { Component } from 'react'
import {
    Alert,
    Button,
    Card,
    Collapse,
    Descriptions,
    Empty,
    InputNumber,
    message,
    Modal,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
} from 'antd'
import {
    DatabaseOutlined,
    ExclamationCircleOutlined,
    HistoryOutlined,
    ReloadOutlined,
    SafetyCertificateOutlined,
    SaveOutlined,
    TableOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import {
    getMyVibeDatasetDepartments,
    getMyVibeDatasetDetail,
    getMyVibeDatasetIngestionErrors,
    getMyVibeDatasetIngestions,
    saveMyVibeDatasetSettings,
} from '../../request'
import {
    DATASET_IMPORT_MODE_META,
    DATASET_POLICY_META,
    DATASET_UPDATE_FREQUENCY_META,
    formatDatasetFieldType,
    getDatasetPolicyMeta,
    normalizeDatasetSettings,
} from './vibeDataset'

const { Panel } = Collapse
const { Text } = Typography

const INGESTION_STATUS_META = {
    prepared: { text: '待上传', color: 'default' },
    uploading: { text: '上传中', color: 'blue' },
    ready: { text: '待提交', color: 'cyan' },
    queued: { text: '已排队', color: 'blue' },
    validating: { text: '校验中', color: 'processing' },
    publishing: { text: '发布中', color: 'processing' },
    success: { text: '成功', color: 'green' },
    failed: { text: '失败', color: 'red' },
    cancelled: { text: '已取消', color: 'default' },
    expired: { text: '已过期', color: 'default' },
}

const SOURCE_TYPE_META = {
    excel: 'Excel',
    csv: 'CSV',
    dingtalk_ai_table: '钉钉 AI 表格',
    image: '图片',
    url: 'URL',
    pdf: 'PDF',
    api: 'API',
    manual: '手工整理',
}

const ACTIVE_INGESTION_STATUSES = new Set([
    'prepared', 'uploading', 'ready', 'queued', 'validating', 'publishing',
])

const DATASET_STATUS_META = {
    draft: { text: '待首次发布', color: 'default' },
    active: { text: '正常', color: 'green' },
    disabled: { text: '已停用', color: 'default' },
}

export default class VibeDatasetPanel extends Component {
    state = {
        loading: true,
        loadError: false,
        detail: null,
        departments: [],
        settings: normalizeDatasetSettings(),
        saving: false,
        ingestionLoading: false,
        ingestionLoadError: false,
        ingestionPage: { pageNum: 1, pageSize: 10, total: 0, list: [] },
        errorVisible: false,
        errorBatch: null,
        errors: [],
        errorLoading: false,
        errorHasMore: false,
        errorNextAfterId: 0,
    }

    pollTimer = null
    mounted = false

    componentDidMount() {
        this.mounted = true
        this.loadAll()
    }

    componentDidUpdate(prevProps) {
        if (String(prevProps.projectId) !== String(this.props.projectId)) {
            this.clearPollTimer()
            this.loadAll()
        }
    }

    componentWillUnmount() {
        this.mounted = false
        this.clearPollTimer()
    }

    clearPollTimer = () => {
        if (this.pollTimer) {
            clearTimeout(this.pollTimer)
            this.pollTimer = null
        }
    }

    loadAll = () => {
        const { projectId } = this.props
        if (!projectId) return
        this.setState({ loading: true, loadError: false })
        Promise.all([
            getMyVibeDatasetDetail({ projectId }),
            getMyVibeDatasetDepartments({}),
        ])
            .then(([detail, departments]) => {
                if (!this.mounted) return
                this.setState({
                    detail,
                    departments: Array.isArray(departments) ? departments : [],
                    settings: normalizeDatasetSettings(detail),
                    loading: false,
                })
            })
            .catch(() => {
                if (this.mounted) this.setState({ loading: false, loadError: true })
            })
        this.loadIngestions(1)
    }

    loadIngestions = (pageNum = 1, showLoading = true) => {
        const { projectId } = this.props
        if (!projectId) return Promise.resolve()
        this.clearPollTimer()
        if (showLoading) {
            this.setState({ ingestionLoading: true, ingestionLoadError: false })
        }
        return getMyVibeDatasetIngestions({ projectId, pageNum, pageSize: 10 })
            .then(page => {
                if (!this.mounted) return
                const ingestionPage = {
                    pageNum: page?.pageNum || pageNum,
                    pageSize: page?.pageSize || 10,
                    total: page?.total || 0,
                    list: Array.isArray(page?.list) ? page.list : [],
                }
                this.setState({ ingestionPage, ingestionLoadError: false }, this.schedulePoll)
            })
            .catch(() => {
                if (this.mounted) this.setState({ ingestionLoadError: true })
            })
            .finally(() => {
                if (this.mounted && showLoading) this.setState({ ingestionLoading: false })
            })
    }

    schedulePoll = () => {
        this.clearPollTimer()
        const active = this.state.ingestionPage.list.some(item => ACTIVE_INGESTION_STATUSES.has(item.status))
        if (!active || !this.mounted) return
        this.pollTimer = setTimeout(() => {
            this.loadIngestions(this.state.ingestionPage.pageNum, false)
        }, 5000)
    }

    updateSetting = (key, value) => {
        this.setState({ settings: { ...this.state.settings, [key]: value } })
    }

    saveSettings = () => {
        const { projectId, onUpdated } = this.props
        const { settings } = this.state
        if (!settings.ownerDepartmentId) {
            message.warning('请选择数据归属一级部门')
            return
        }
        this.setState({ saving: true })
        saveMyVibeDatasetSettings({ projectId, ...settings })
            .then(detail => {
                message.success('数据集设置已保存')
                this.setState({
                    detail,
                    settings: normalizeDatasetSettings(detail),
                })
                if (onUpdated) onUpdated()
            })
            .catch(() => {})
            .finally(() => this.setState({ saving: false }))
    }

    openErrors = batch => {
        this.setState({
            errorVisible: true,
            errorBatch: batch,
            errors: [],
            errorHasMore: false,
            errorNextAfterId: 0,
        }, () => this.loadErrors(true))
    }

    loadErrors = (reset = false) => {
        const { projectId } = this.props
        const { errorBatch, errorNextAfterId, errorLoading } = this.state
        if (!errorBatch || errorLoading) return
        const afterId = reset ? 0 : errorNextAfterId
        this.setState({ errorLoading: true })
        getMyVibeDatasetIngestionErrors({
            projectId,
            ingestionId: errorBatch.ingestionId,
            afterId,
            limit: 100,
        })
            .then(result => {
                const incoming = Array.isArray(result?.list) ? result.list : []
                this.setState(prevState => ({
                    errors: reset ? incoming : prevState.errors.concat(incoming),
                    errorHasMore: !!result?.hasMore,
                    errorNextAfterId: result?.nextAfterId || afterId,
                }))
            })
            .catch(() => {})
            .finally(() => this.setState({ errorLoading: false }))
    }

    formatDate = value => value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-'

    renderPolicyAlert = () => {
        const { settings } = this.state
        const policy = getDatasetPolicyMeta(settings.sensitivityLevel)
        return (
            <Alert
                type={settings.sensitivityLevel === 'L3' ? 'error' : 'info'}
                showIcon
                icon={<SafetyCertificateOutlined />}
                message={policy.text}
                description={policy.summary}
                action={this.props.onManagePermissions ? (
                    <Button size="small" onClick={this.props.onManagePermissions}>管理项目权限</Button>
                ) : null}
            />
        )
    }

    renderDatasetStatus = status => {
        const meta = DATASET_STATUS_META[status] || { text: status || '-', color: 'default' }
        return <Tag color={meta.color}>{meta.text}</Tag>
    }

    renderSettings = () => {
        const { detail, departments, settings, saving } = this.state
        return (
            <Card
                bordered={false}
                className="profile-vibe-detail-card profile-vibe-dataset-section"
                title={<span><DatabaseOutlined /> 数据集设置</span>}
                extra={(
                    <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={this.saveSettings}>
                        保存
                    </Button>
                )}
            >
                {this.renderPolicyAlert()}
                <div className="profile-vibe-dataset-setting-grid">
                    <div>
                        <div className="profile-vibe-form-label">敏感级</div>
                        <Select
                            value={settings.sensitivityLevel}
                            style={{ width: '100%' }}
                            onChange={value => this.updateSetting('sensitivityLevel', value)}
                        >
                            {Object.entries(DATASET_POLICY_META).map(([value, item]) => (
                                <Select.Option key={value} value={value}>{item.text}</Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <div className="profile-vibe-form-label">数据归属一级部门</div>
                        <Select
                            value={settings.ownerDepartmentId}
                            style={{ width: '100%' }}
                            onChange={value => this.updateSetting('ownerDepartmentId', value)}
                        >
                            {departments.map(item => (
                                <Select.Option key={String(item.departmentId)} value={item.departmentId}>
                                    {item.departmentName || item.departmentId}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <div className="profile-vibe-form-label">默认导入模式</div>
                        <Select
                            value={settings.defaultImportMode}
                            style={{ width: '100%' }}
                            onChange={value => this.updateSetting('defaultImportMode', value)}
                        >
                            {Object.entries(DATASET_IMPORT_MODE_META).map(([value, text]) => (
                                <Select.Option key={value} value={value}>{text}</Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <div className="profile-vibe-form-label">更新频率</div>
                        <Select
                            value={settings.updateFrequency}
                            style={{ width: '100%' }}
                            onChange={value => this.updateSetting('updateFrequency', value)}
                        >
                            {Object.entries(DATASET_UPDATE_FREQUENCY_META).map(([value, text]) => (
                                <Select.Option key={value} value={value}>{text}</Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <div className="profile-vibe-form-label">断更宽限时间（小时）</div>
                        <InputNumber
                            min={0}
                            max={720}
                            value={settings.gracePeriodHours}
                            style={{ width: '100%' }}
                            onChange={value => this.updateSetting('gracePeriodHours', value)}
                        />
                    </div>
                </div>
                <Descriptions
                    className="profile-vibe-dataset-stats"
                    size="small"
                    column={{ xs: 1, sm: 2, md: 3 }}
                >
                    <Descriptions.Item label="数据状态">{this.renderDatasetStatus(detail?.datasetStatus)}</Descriptions.Item>
                    <Descriptions.Item label="结构版本">{detail?.schemaVersion ?? '-'}</Descriptions.Item>
                    <Descriptions.Item label="数据版本">{detail?.publishedVersion ?? '-'}</Descriptions.Item>
                    <Descriptions.Item label="最近上传">{this.formatDate(detail?.lastSuccessfulUploadAt)}</Descriptions.Item>
                    <Descriptions.Item label="下次应更新">{this.formatDate(detail?.nextExpectedUploadAt)}</Descriptions.Item>
                    <Descriptions.Item label="共享数据库">
                        <Text code copyable={!!detail?.sharedDatabaseName}>{detail?.sharedDatabaseName || '-'}</Text>
                    </Descriptions.Item>
                </Descriptions>
            </Card>
        )
    }

    renderSchema = () => {
        const tables = Array.isArray(this.state.detail?.tables) ? this.state.detail.tables : []
        const fieldColumns = [
            {
                title: '字段',
                key: 'field',
                width: 180,
                render: (_, record) => (
                    <div>
                        <div>{record.displayName || record.fieldName || '-'}</div>
                        <Text type="secondary" code>{record.fieldName || '-'}</Text>
                    </div>
                ),
            },
            {
                title: '类型',
                key: 'type',
                width: 120,
                render: (_, record) => <Text code>{formatDatasetFieldType(record)}</Text>,
            },
            {
                title: '约束',
                key: 'constraint',
                width: 150,
                render: (_, record) => (
                    <Space size={4} wrap>
                        {record.primaryKeyField ? <Tag color="gold">主键</Tag> : null}
                        <Tag>{record.nullable ? '可空' : '必填'}</Tag>
                    </Space>
                ),
            },
            {
                title: '敏感级',
                dataIndex: 'effectiveSensitivityLevel',
                width: 90,
                render: value => {
                    const meta = getDatasetPolicyMeta(value)
                    return <Tag color={meta.color}>{value || 'L1'}</Tag>
                },
            },
            {
                title: '说明',
                dataIndex: 'description',
                render: value => value || <Text type="secondary">-</Text>,
            },
        ]
        return (
            <Card
                bordered={false}
                className="profile-vibe-detail-card profile-vibe-dataset-section"
                title={<span><TableOutlined /> 表与字段</span>}
            >
                {tables.length ? (
                    <Collapse bordered={false} className="profile-vibe-dataset-schema">
                        {tables.map(table => (
                            <Panel
                                key={String(table.id)}
                                header={(
                                    <div className="profile-vibe-dataset-table-head">
                                        <div>
                                            <Text strong>{table.displayName || table.logicalName}</Text>
                                            <Text type="secondary" code>{table.logicalName}</Text>
                                        </div>
                                        <Space size={4} wrap>
                                            <Tag>{table.publishedRowCount || 0} 行</Tag>
                                            <Tag>结构 v{table.schemaVersion || 1}</Tag>
                                            <Tag color={getDatasetPolicyMeta(table.effectiveSensitivityLevel).color}>
                                                {table.effectiveSensitivityLevel || 'L1'}
                                            </Tag>
                                        </Space>
                                    </div>
                                )}
                            >
                                {table.description ? (
                                    <div className="profile-vibe-dataset-table-description">{table.description}</div>
                                ) : null}
                                <Table
                                    rowKey={record => String(record.id)}
                                    size="small"
                                    columns={fieldColumns}
                                    dataSource={Array.isArray(table.fields) ? table.fields : []}
                                    pagination={false}
                                    scroll={{ x: 760 }}
                                    locale={{ emptyText: '暂无字段' }}
                                />
                            </Panel>
                        ))}
                    </Collapse>
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无已发布数据表" />
                )}
            </Card>
        )
    }

    renderIngestionStatus = status => {
        const meta = INGESTION_STATUS_META[status] || { text: status || '-', color: 'default' }
        return <Tag color={meta.color}>{meta.text}</Tag>
    }

    renderIngestions = () => {
        const {
            ingestionLoading, ingestionLoadError, ingestionPage,
        } = this.state
        const columns = [
            {
                title: '来源',
                key: 'source',
                width: 190,
                render: (_, record) => (
                    <div>
                        <div>{record.sourceName || '-'}</div>
                        <Text type="secondary">{SOURCE_TYPE_META[record.sourceType] || record.sourceType || '-'}</Text>
                    </div>
                ),
            },
            {
                title: '状态',
                dataIndex: 'status',
                width: 90,
                render: this.renderIngestionStatus,
            },
            {
                title: '分片',
                key: 'chunks',
                width: 90,
                render: (_, record) => `${record.receivedChunkCount || 0}/${record.expectedChunkCount || 0}`,
            },
            {
                title: '数据行',
                key: 'rows',
                width: 150,
                render: (_, record) => (
                    <div>
                        <div>写入 {record.writtenRowCount || 0}</div>
                        <Text type={record.rejectedRowCount ? 'danger' : 'secondary'}>
                            拒绝 {record.rejectedRowCount || 0}
                        </Text>
                    </div>
                ),
            },
            {
                title: '模式',
                dataIndex: 'importMode',
                width: 130,
                render: value => DATASET_IMPORT_MODE_META[value] || value || '-',
            },
            {
                title: '更新时间',
                dataIndex: 'updateTime',
                width: 155,
                render: this.formatDate,
            },
            {
                title: '操作',
                key: 'action',
                width: 90,
                fixed: 'right',
                render: (_, record) => (
                    record.status === 'failed' || Number(record.rejectedRowCount || 0) > 0
                        ? (
                            <Button
                                type="link"
                                size="small"
                                icon={<ExclamationCircleOutlined />}
                                onClick={() => this.openErrors(record)}
                            >
                                错误
                            </Button>
                        ) : null
                ),
            },
        ]
        return (
            <Card
                bordered={false}
                className="profile-vibe-detail-card profile-vibe-dataset-section"
                title={<span><HistoryOutlined /> 导入记录</span>}
                extra={(
                    <Button
                        icon={<ReloadOutlined />}
                        loading={ingestionLoading}
                        onClick={() => this.loadIngestions(ingestionPage.pageNum)}
                    >
                        刷新
                    </Button>
                )}
            >
                {ingestionLoadError ? (
                    <Alert
                        type="error"
                        showIcon
                        message="导入记录加载失败"
                        action={<Button size="small" onClick={() => this.loadIngestions(1)}>重试</Button>}
                        style={{ marginBottom: 12 }}
                    />
                ) : null}
                <Table
                    rowKey={record => String(record.id)}
                    size="small"
                    loading={ingestionLoading}
                    columns={columns}
                    dataSource={ingestionPage.list}
                    scroll={{ x: 900 }}
                    locale={{ emptyText: '暂无导入记录' }}
                    pagination={{
                        current: ingestionPage.pageNum,
                        pageSize: ingestionPage.pageSize,
                        total: ingestionPage.total,
                        showSizeChanger: false,
                        onChange: pageNum => this.loadIngestions(pageNum),
                    }}
                />
            </Card>
        )
    }

    renderErrorModal = () => {
        const {
            errorVisible, errorBatch, errors, errorLoading, errorHasMore,
        } = this.state
        const columns = [
            { title: '行号', dataIndex: 'sourceRowNo', width: 80, render: value => value ?? '-' },
            { title: '表', dataIndex: 'logicalTableName', width: 130, render: value => <Text code>{value || '-'}</Text> },
            { title: '字段', dataIndex: 'fieldName', width: 130, render: value => <Text code>{value || '-'}</Text> },
            { title: '错误代码', dataIndex: 'errorCode', width: 150, render: value => <Text code>{value || '-'}</Text> },
            { title: '错误说明', dataIndex: 'errorMessage', width: 260 },
            { title: '值预览', dataIndex: 'valuePreview', width: 140, render: value => value || '-' },
            { title: '时间', dataIndex: 'createTime', width: 155, render: this.formatDate },
        ]
        return (
            <Modal
                visible={errorVisible}
                width={1050}
                title={`导入错误：${errorBatch?.sourceName || errorBatch?.ingestionId || '-'}`}
                footer={[
                    errorHasMore ? (
                        <Button key="more" loading={errorLoading} onClick={() => this.loadErrors(false)}>
                            加载更多
                        </Button>
                    ) : null,
                    <Button key="close" onClick={() => this.setState({ errorVisible: false })}>关闭</Button>,
                ].filter(Boolean)}
                onCancel={() => this.setState({ errorVisible: false })}
                destroyOnClose
            >
                {errorBatch?.errorSummary ? (
                    <Alert type="error" showIcon message={errorBatch.errorSummary} style={{ marginBottom: 12 }} />
                ) : null}
                <Table
                    rowKey={record => String(record.id)}
                    size="small"
                    loading={errorLoading && !errors.length}
                    columns={columns}
                    dataSource={errors}
                    pagination={false}
                    scroll={{ x: 1050, y: 480 }}
                    locale={{ emptyText: '没有结构化错误明细' }}
                />
            </Modal>
        )
    }

    render() {
        const { loading, loadError, detail } = this.state
        if (loading) {
            return <div className="profile-vibe-detail-loading"><Spin /></div>
        }
        if (loadError) {
            return (
                <Alert
                    type="error"
                    showIcon
                    message="数据集详情加载失败"
                    action={<Button size="small" onClick={this.loadAll}>重试</Button>}
                />
            )
        }
        if (!detail) return null
        return (
            <>
                {this.renderSettings()}
                {this.renderSchema()}
                {this.renderIngestions()}
                {this.renderErrorModal()}
            </>
        )
    }
}
